/**
 * Pull the structured interactive payload off a message's dataJson — shape
 * mirrors what `cascade-engine.ts` `recordBotOutbound` writes for outbound
 * interactive sends (button or list). Returns null when the message isn't
 * an interactive bot send, or the payload is malformed/empty.
 */
export type InteractivePayload =
  | { interactiveType: 'button'; buttons: Array<{ id: string; title: string }> }
  | {
      interactiveType: 'list';
      listButtonText: string;
      rows: Array<{ id: string; title: string; description?: string }>;
    };

export function parseInteractivePayload(m: {
  messageType: string | null;
  dataJson?: string | null;
}): InteractivePayload | null {
  if (m.messageType !== 'interactive' || !m.dataJson) return null;
  try {
    const parsed = JSON.parse(m.dataJson) as {
      interactiveType?: 'button' | 'list';
      buttons?: Array<{ id?: string; title?: string }>;
      rows?: Array<{ id?: string; title?: string; description?: string }>;
      listButtonText?: string;
    };
    if (parsed.interactiveType === 'list') {
      if (!Array.isArray(parsed.rows) || parsed.rows.length === 0) return null;
      const rows = parsed.rows
        .filter((r) => r && (r.title ?? '').trim().length > 0)
        .map((r, i) => ({
          id: String(r.id ?? `row_${i}`),
          title: String(r.title ?? ''),
          ...(r.description ? { description: String(r.description) } : {}),
        }));
      if (rows.length === 0) return null;
      return {
        interactiveType: 'list',
        listButtonText: (parsed.listButtonText ?? '').trim() || 'Choose',
        rows,
      };
    }
    if (!Array.isArray(parsed.buttons) || parsed.buttons.length === 0) return null;
    return {
      interactiveType: 'button',
      buttons: parsed.buttons
        .filter((b) => b && (b.title ?? '').trim().length > 0)
        .map((b, i) => ({ id: String(b.id ?? `btn_${i}`), title: String(b.title ?? '') })),
    };
  } catch {
    return null;
  }
}
